import { useMemo, useState } from 'react';
import { formatCustomerLabel } from '@/lib/customer-label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxesStacked,
  faBoxOpen,
  faCalendarDays,
  faChevronRight,
  faCubesStacked,
  faTriangleExclamation,
  faTruckLoading,
} from '@fortawesome/free-solid-svg-icons';
import { receivingLotService, type ReceivingLotSortField } from '@/services/receiving-lot.service';
import { customerService } from '@/services/customer.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { SkeletonList } from '@/components/skeletons/SkeletonList';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

type DatePreset = 'today' | 'yesterday' | '7d' | '30d' | 'custom' | 'all';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function presetRange(preset: DatePreset): { from?: string; to?: string } {
  const now = new Date();
  const today = isoDate(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: isoDate(y), to: isoDate(y) };
    }
    case '7d': {
      const from = new Date(now);
      from.setDate(from.getDate() - 6);
      return { from: isoDate(from), to: today };
    }
    case '30d': {
      const from = new Date(now);
      from.setDate(from.getDate() - 29);
      return { from: isoDate(from), to: today };
    }
    default:
      return {};
  }
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  RECEIVING_COMPLETE: 'Receiving Complete',
  CLOSED: 'Closed',
};

const SORT_OPTIONS: { value: ReceivingLotSortField; label: string }[] = [
  { value: 'receivedAt', label: 'Received Date' },
  { value: 'lotNumber', label: 'Lot Number' },
  { value: 'customerName', label: 'Customer' },
  { value: 'equipmentCount', label: 'Tool Count' },
  { value: 'createdAt', label: 'Created' },
];

export function ReceivingLotsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const preset = (searchParams.get('preset') as DatePreset | null) ?? 'all';
  const receivedFrom = searchParams.get('from') ?? '';
  const receivedTo = searchParams.get('to') ?? '';
  const sortBy = (searchParams.get('sortBy') as ReceivingLotSortField | null) ?? 'receivedAt';
  const sortDir = (searchParams.get('sortDir') as 'asc' | 'desc' | null) ?? 'desc';

  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebounce(searchInput, 350);

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!('page' in patch)) next.set('page', '1');
    setSearchParams(next);
  };

  // Debounced search syncs into URL state once settled — avoids a request per keystroke.
  useMemo(() => {
    if (debouncedSearch !== search) updateParams({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const applyPreset = (p: DatePreset) => {
    const range = presetRange(p);
    updateParams({ preset: p, from: range.from, to: range.to });
  };

  const { data: customers } = useQuery({
    queryKey: ['customers-all-for-receiving-lots'],
    queryFn: () => customerService.list({ pageSize: 100 }),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'receiving-lots',
      page,
      search,
      customerId,
      receivedFrom,
      receivedTo,
      sortBy,
      sortDir,
    ],
    queryFn: () =>
      receivingLotService.list({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
        customerId: customerId || undefined,
        receivedFrom: receivedFrom || undefined,
        receivedTo: receivedTo || undefined,
        sortBy,
        sortDir,
      }),
  });

  const hasFilters = Boolean(search || customerId || receivedFrom || receivedTo);
  const clearFilters = () => {
    setSearchInput('');
    setSearchParams({});
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Receiving Lots"
        description="Equipment received in bulk, automatically organized by model."
      />

      {/* Date presets */}
      <div className="flex flex-wrap items-center gap-2">
        <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5 text-muted-foreground" />
        {(['today', 'yesterday', '7d', '30d', 'all'] as DatePreset[]).map((p) => (
          <button
            key={p}
            onClick={() => applyPreset(p)}
            className={`min-h-[36px] rounded-full border px-3 text-xs font-medium transition-colors ${
              preset === p
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            {p === 'today' && 'Today'}
            {p === 'yesterday' && 'Yesterday'}
            {p === '7d' && 'Last 7 Days'}
            {p === '30d' && 'Last 30 Days'}
            {p === 'all' && 'All Dates'}
          </button>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="date"
            value={receivedFrom}
            onChange={(e) => updateParams({ preset: 'custom', from: e.target.value })}
            className="h-9 rounded-lg border border-border bg-card px-2 text-xs"
            aria-label="Received from"
          />
          <span>to</span>
          <input
            type="date"
            value={receivedTo}
            onChange={(e) => updateParams({ preset: 'custom', to: e.target.value })}
            className="h-9 rounded-lg border border-border bg-card px-2 text-xs"
            aria-label="Received to"
          />
        </div>
      </div>

      {/* Search + customer filter + sort */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search lot number, customer, or model..."
          />
          <select
            value={customerId}
            onChange={(e) => updateParams({ customerId: e.target.value })}
            className="h-9 min-w-[180px] rounded-lg border border-border bg-card px-3 text-sm"
            aria-label="Filter by customer"
          >
            <option value="">All Customers</option>
            {customers?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {formatCustomerLabel(c)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => updateParams({ sortBy: e.target.value })}
            className="h-9 rounded-lg border border-border bg-card px-3 text-sm"
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Sort: {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => updateParams({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' })}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-sm hover:bg-muted"
            aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {isLoading && <SkeletonList rows={6} />}

      {isError && (
        <EmptyState
          icon={faTriangleExclamation}
          title="Failed to load Receiving Lots"
          description="Something went wrong. Try refreshing the page."
        />
      )}

      {!isLoading && !isError && data && (
        <>
          <p className="text-sm text-muted-foreground">
            {data.total} lot{data.total === 1 ? '' : 's'} found
          </p>

          {data.data.length === 0 ? (
            <EmptyState
              icon={faBoxOpen}
              title="No Receiving Lots found"
              description={
                hasFilters ? 'Try adjusting your filters.' : 'No equipment has been received yet.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {data.data.map((lot) => (
                <button
                  key={lot.id}
                  onClick={() => navigate(ROUTES.OPS_RECEIVING_LOT_DETAIL.replace(':id', lot.id))}
                  className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-mono text-sm font-semibold text-foreground">
                        {lot.lotNumber}
                      </p>
                      <p className="text-sm text-muted-foreground">{lot.customerName}</p>
                      {lot.customerLocationName && (
                        <p className="text-xs text-muted-foreground">{lot.customerLocationName}</p>
                      )}
                    </div>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      {STATUS_LABEL[lot.status] ?? lot.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span>{new Date(lot.receivedAt).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faBoxesStacked} className="h-3 w-3" />
                      {lot.equipmentCount} tools
                    </span>
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faCubesStacked} className="h-3 w-3" />
                      {lot.modelBatchCount} model{lot.modelBatchCount === 1 ? '' : 's'}
                    </span>
                    {lot.arrivalCount > 1 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <FontAwesomeIcon icon={faTruckLoading} className="h-3 w-3" />
                        {lot.arrivalCount} arrivals · latest{' '}
                        {new Date(lot.latestArrivalAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {lot.modelBatchPreview.map((b) => (
                      <span
                        key={b.id}
                        className="rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-foreground"
                      >
                        {b.equipmentModelName ?? 'Unknown model'} · {b.equipmentCount}
                      </span>
                    ))}
                    {lot.modelBatchPreviewRemaining > 0 && (
                      <span className="rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                        +{lot.modelBatchPreviewRemaining} more
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-end text-xs text-primary">
                    View details{' '}
                    <FontAwesomeIcon icon={faChevronRight} className="ml-1 h-2.5 w-2.5" />
                  </div>
                </button>
              ))}
            </div>
          )}

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={(p) => updateParams({ page: String(p) })}
          />
        </>
      )}
    </div>
  );
}
